import {createUniqueKeyFromObject} from './createUniqueKeyFromObject';

export interface CacheKeyConfig {
  uniqueIdentifier: string;
  params: Record<string, unknown>;
}

interface InternalCacheKeyConfig extends Pick<CacheKeyConfig, 'params'> {
  uniqueIdentifier?: string;
}

export const createCacheKey = ({
  uniqueIdentifier,
  params,
}: InternalCacheKeyConfig) => {
  if (!uniqueIdentifier) {
    throw new Error(
      'uniqueIdentifier is not defined but always required. This should be generated using the data-fetcher-dynamic-grpc-request-import babel transform',
    );
  }

  let _params = params;

  /**
   * BACKGROUND:
   * The BasketToken is comprized of two fields. A `hash` and a `token`.
   *  - `hash` is the hash of the contents of the basket
   * - `token` contains the encoded basket contents + a timestamp
   * We don't want to include the `token` in the cache key as it contains the timestamp which changes every few minutes even if there are no changes to the basket contents.
   * Thus we want to exclude the `token` from the cache key, and only include the `hash`. So that we refetch the requests which uses basketToken only when the basket contents change.
   *
   * SOLUTION:
   * Ideally to remove the `token` from the cache key, we could use the `transformCacheKey` config to explicitly remove the `token` from the 'basketToken' request params.
   * But since we have a lot of requests which uses `basketToken` and we don't want to add the `transformCacheKey` config to all of them, we are doing it here.
   * This makes sure we can use basketToken in the request params without having to worry about the unnecessary cache invalidations.
   *
   * WARNING:
   * This solution assumes that request parameter would be named `basketToken`. This holds true for all the requests in the codebase as of now.
   * If this changes in the future, this solution would break for that particular request.
   */
  if (
    params &&
    params.basketToken &&
    typeof params.basketToken === 'object' &&
    'basketToken' in params
  ) {
    _params = {
      ...params,
      basketToken: {
        ...params.basketToken,
        token: undefined,
      },
    };
  }

  const paramsHash = createUniqueKeyFromObject(_params);
  return `${uniqueIdentifier}:${paramsHash}`;
};
